import React, { useCallback, useEffect } from 'react';

import { useDispatch } from 'react-redux';
import styled, { css } from 'styled-components';

import { useQuickViewContext } from '~/quickView/components/QuickViewContext';
import { loadCart } from '~/techstyle-shared/react-cart';
import { Button, mobile } from '~/techstyle-shared/react-components';
import { FormattedMessage, useIntl } from '~/techstyle-shared/react-intl';

import errorMessages from './errorMessages';
import {
  useProductDetailCTAContext,
  ProductDetailCTAErrorTypes,
} from './ProductDetailCTAContext';

const UpdateBagButton = styled(Button).attrs({
  variant: 'primaryAddToBag',
  size: 'lg',
})`
  border-radius: 4px;
  margin-top: 32px;
  width: 100%;

  ${mobile`
    margin-top: 16px;
  `}
`;

const labelStyle = css`
  font-size: 18px;
  font-weight: bold;
  letter-spacing: ${-0.4 / 18}em;
  color: ${({ theme }) => theme.colors.textLight};
`;

function UpdateBagCTA() {
  const intl = useIntl();

  const quickViewContext = useQuickViewContext();
  const cartItem = quickViewContext?.cartItem;
  const dispatch = useDispatch();

  const {
    addToCart,
    handleSharedValidation,
    isLoading,
    setErrorMessage,
    onSuccessfulAddToCart,
    deleteFromCart,
    hasError,
  } = useProductDetailCTAContext();
  const disabled = isLoading;

  useEffect(() => {
    if (!hasError) {
      setErrorMessage(null);
    }
  }, [hasError, setErrorMessage]);

  const handleCTAClick = useCallback(async () => {
    try {
      await handleSharedValidation();
      await deleteFromCart(cartItem);
      await addToCart({ applyMemberCredit: cartItem.tokenQtyApplied > 0 });

      onSuccessfulAddToCart && onSuccessfulAddToCart();

      await dispatch(loadCart({ params: { recalcCart: true, noCache: true } }));
    } catch (error) {
      if (error.message === ProductDetailCTAErrorTypes.SelectorError) {
        setErrorMessage(intl.formatMessage(errorMessages.sizeSelectorError));
      }
    }
  }, [
    handleSharedValidation,
    deleteFromCart,
    cartItem,
    addToCart,
    onSuccessfulAddToCart,
    setErrorMessage,
    intl,
    dispatch,
  ]);

  return (
    <UpdateBagButton
      data-autotag="update-bag"
      labelStyle={labelStyle}
      onClick={handleCTAClick}
      disabled={disabled}
    >
      <FormattedMessage
        id="app_pdp.update_in_bag"
        defaultMessage="Update in Bag"
      />
    </UpdateBagButton>
  );
}

export default UpdateBagCTA;
